import React, { useEffect, useMemo, useState } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { DATE_RANGE_PRESETS, findMatchingPreset, formatDateRange } from './dateRangePresets';
import type { DateRange, DateRangeSelectorProps } from './types';

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value = { start: null, end: null },
  onChange,
  label = 'Date Range',
  size = 'medium',
  disabled = false,
  disableClearable = false,
  error = false,
  helperText,
  minDate,
  maxDate,
  fullWidth = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [localRange, setLocalRange] = useState<DateRange>(value);
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Use md breakpoint for better layout

  // Update local state when value prop changes from outside
  useEffect(() => {
    // Check if current value matches any preset
    const matchingPreset = findMatchingPreset(value);
    setIsCustomSelected(!matchingPreset && (!!value.start || !!value.end));
  }, [value]);

  // Determine current selection based on local state for highlighting in popover
  const currentLocalPreset = useMemo(() => {
    return findMatchingPreset(localRange);
  }, [localRange]);

  // Determine the actually applied preset (for showing check mark)
  const currentAppliedPreset = useMemo(() => {
    return findMatchingPreset(value);
  }, [value]);

  // Check if the currently applied value is custom (doesn't match any preset)
  const _isAppliedCustom = !currentAppliedPreset && (!!value.start || !!value.end);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Reset local state to match the actual value when opening
    setLocalRange(value);
    // Check if current value is custom
    const matchingPreset = findMatchingPreset(value);
    setIsCustomSelected(!matchingPreset && (!!value.start || !!value.end));
  };

  const handleClose = () => {
    setAnchorEl(null);
    // Reset local state to match the actual value when closing without applying
    setLocalRange(value);
  };

  const handlePresetSelect = (preset: (typeof DATE_RANGE_PRESETS)[0]) => {
    const range = preset.getRange();
    setLocalRange(range);
    onChange(range);
    setIsCustomSelected(false);
    handleClose();
  };

  const handleStartDateChange = (date: Date | null) => {
    const newRange = { ...localRange, start: date };
    setLocalRange(newRange);
    setIsCustomSelected(true);
  };

  const handleEndDateChange = (date: Date | null) => {
    const newRange = { ...localRange, end: date };
    setLocalRange(newRange);
    setIsCustomSelected(true);
  };

  const handleApply = () => {
    onChange(localRange);
    handleClose();
  };

  const handleClear = () => {
    const emptyRange = { start: null, end: null };
    setLocalRange(emptyRange);
    onChange(emptyRange);
    setIsCustomSelected(false);
    handleClose();
  };

  // Display value should always show the actual applied value, not local state
  const displayValue = findMatchingPreset(value)?.label || formatDateRange(value);
  const hasValue = value.start || value.end;
  const open = Boolean(anchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
        <Stack spacing={1}>
          {/* Input Field styled like other filters */}
          <TextField
            size={size}
            variant="outlined"
            label={label}
            value={hasValue ? displayValue : ''}
            onClick={handleOpen}
            disabled={disabled}
            error={error}
            helperText={helperText}
            fullWidth={fullWidth}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {hasValue && !disableClearable && (
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleClear();
                      }}
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      disabled={disabled}
                      edge="end"
                      sx={{ mr: 0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <CalendarTodayIcon
                    sx={{
                      color: 'action.active',
                      cursor: 'pointer',
                      fontSize: 20,
                    }}
                  />
                </InputAdornment>
              ),
              sx: {
                cursor: 'pointer',
                '& input': {
                  cursor: 'pointer',
                },
              },
            }}
            InputLabelProps={{
              shrink: !!hasValue,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover': {
                  '& fieldset': {
                    borderColor: error ? 'error.main' : 'primary.main',
                  },
                },
              },
            }}
          />

          {/* Popover with presets and custom date inputs */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: isMobile ? 320 : 560,
                  maxWidth: isMobile ? '95vw' : 600,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: theme.shadows[8],
                },
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              {isMobile ? (
                // Mobile: Stacked layout
                <Stack spacing={2}>
                  {/* Quick Selects */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Quick Select
                    </Typography>
                    <Stack spacing={0.5}>
                      {DATE_RANGE_PRESETS.map(preset => (
                        <ListItemButton
                          key={preset.value}
                          onClick={() => {
                            handlePresetSelect(preset);
                          }}
                          selected={false} // Don't use selected state for temporary selection
                          sx={{
                            borderRadius: 1,
                            py: 0.5,
                            minHeight: 36,
                            // Highlight if this is temporarily selected (but not applied)
                            bgcolor:
                              currentLocalPreset?.value === preset.value && !isCustomSelected
                                ? 'action.hover'
                                : 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {currentAppliedPreset?.value === preset.value && (
                              <CheckIcon fontSize="small" color="primary" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={preset.label}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Custom Date Inputs */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        display: 'block',
                        mb: 2,
                      }}
                    >
                      Custom Range
                    </Typography>
                    <Stack spacing={2}>
                      <DatePicker
                        label="Start Date"
                        value={localRange.start}
                        onChange={handleStartDateChange}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                        minDate={minDate}
                        maxDate={localRange.end || maxDate || undefined}
                        disabled={disabled}
                      />

                      <DatePicker
                        label="End Date"
                        value={localRange.end}
                        onChange={handleEndDateChange}
                        slotProps={{
                          textField: {
                            size: 'small',
                            fullWidth: true,
                          },
                        }}
                        minDate={localRange.start || minDate || undefined}
                        maxDate={maxDate || undefined}
                        disabled={disabled}
                      />
                    </Stack>
                  </Box>

                  {/* Action Buttons */}
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="text" onClick={handleClear} sx={{ textTransform: 'none' }}>
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleApply}
                      sx={{ textTransform: 'none' }}
                    >
                      Apply
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                // Desktop: Side-by-side layout
                <Stack direction="row" spacing={3}>
                  {/* Left: Quick Selects */}
                  <Box sx={{ minWidth: 160 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Quick Select
                    </Typography>
                    <Stack spacing={0.5}>
                      {DATE_RANGE_PRESETS.map(preset => (
                        <ListItemButton
                          key={preset.value}
                          onClick={() => {
                            handlePresetSelect(preset);
                          }}
                          selected={false} // Don't use selected state for temporary selection
                          sx={{
                            borderRadius: 1,
                            py: 0.5,
                            minHeight: 36,
                            // Highlight if this is temporarily selected (but not applied)
                            bgcolor:
                              currentLocalPreset?.value === preset.value && !isCustomSelected
                                ? 'action.hover'
                                : 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            {currentAppliedPreset?.value === preset.value && (
                              <CheckIcon fontSize="small" color="primary" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={preset.label}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </Stack>
                  </Box>

                  {/* Vertical Divider */}
                  <Divider orientation="vertical" flexItem />

                  {/* Right: Custom Date Inputs */}
                  <Box sx={{ flex: 1, maxWidth: 400 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        display: 'block',
                        mb: 2,
                      }}
                    >
                      Custom Range
                    </Typography>

                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2}>
                        <DatePicker
                          label="Start Date"
                          value={localRange.start}
                          onChange={handleStartDateChange}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              sx: { maxWidth: 180 },
                            },
                          }}
                          minDate={minDate}
                          maxDate={localRange.end || maxDate || undefined}
                          disabled={disabled}
                        />

                        <DatePicker
                          label="End Date"
                          value={localRange.end}
                          onChange={handleEndDateChange}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              sx: { maxWidth: 180 },
                            },
                          }}
                          minDate={localRange.start || minDate || undefined}
                          maxDate={maxDate || undefined}
                          disabled={disabled}
                        />
                      </Stack>

                      {/* Current selection display */}
                      {(localRange.start || localRange.end) && (
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: '#fafafa',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Selected: {formatDateRange(localRange)}
                          </Typography>
                        </Box>
                      )}

                      {/* Action Buttons */}
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="text" onClick={handleClear} sx={{ textTransform: 'none' }}>
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleApply}
                          sx={{ textTransform: 'none' }}
                        >
                          Apply
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          </Popover>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};
